import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { resetNavbarParams, setText } from "../../../store/navbar";
import { setLinks, showLinks } from "../../../store/sidebar";
import ClassroomCard from "../../../components/Classroom/StudentClassroom/ClassroomCard";
import PaginationComponent from "../../../components/Classroom/PaginationComponent";
import {
  cloneClassroom,
  deleteSingleClassroom,
  getAllClassrooms,
  updateClassroom,
} from "../../../utils/classrooms";
import { useSelector } from "react-redux";
import { fetchCategories } from "../../../store/categoriesSlice";
import {
  getUserInformation,
  getUserPermission,
  getUserRole,
} from "../../../store/userRole";
import { getMainScreenEndponts } from "../../../constants/sidebar";
import StyledOutlineButton from "../../../components/Classroom/StyledOutlineButton";
import SearchBar from "../../../components/Classroom/SearchBar";
import FiltersDropdown from "../../../components/Classroom/FiltersDropdown";
import { permissionsParser } from "../../../utils/permissionsParser";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteModal from "../../../components/Classroom/DeleteModal";
import ShareModal from "../../../components/Classroom/ShareModal";
import Loader from "../../../components/Loader";

const MyClassrooms = () => {
  const roleOfTheUser = useSelector(getUserRole);
  const rolePermissions = useSelector(getUserPermission);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [numOfPage, setNumOfPage] = useState(0);
  const [pageSize] = useState(8);

  const getStoredPageNo = () => {
    const storedPage = localStorage.getItem("classroomPageNo");
    if (storedPage && !Number.isNaN(storedPage)) {
      return parseInt(storedPage);
    }
    return 1;
  };
  const [page, setPage] = useState(getStoredPageNo);
  const permissions = permissionsParser("Classroom", rolePermissions);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const userInformation = useSelector(getUserInformation);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [rowId, setRowId] = useState("");
  const [openShareModal, setOpenShareModal] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const categoriesStatus = useSelector((state) => state.categories.status);

  useEffect(() => {
    if (categoriesStatus === "idle") {
      dispatch(fetchCategories());
    }
  }, [categoriesStatus, dispatch]);

  useEffect(() => {
    const heading = "my-classrooms";
    dispatch(setText(heading));
    dispatch(resetNavbarParams());
    const mainEndpoints = getMainScreenEndponts(roleOfTheUser, rolePermissions);
    const indOfItem = mainEndpoints?.findIndex(
      (item) => item.value === "My Classrooms"
    );
    if (indOfItem !== -1) {
      mainEndpoints[indOfItem].selected = true;
    }
    dispatch(setLinks(mainEndpoints));
    dispatch(showLinks());
  }, [dispatch, roleOfTheUser, rolePermissions]);

  const handleSearch = () => {
    setSearchTerm(searchText);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    if (newValue === "") {
      setSearchTerm("");
    }
  };

  useEffect(() => {
    localStorage.setItem("classroomPageNo", page);
  }, [page]);

  const getClassrooms = async (pageNo, searchedText, status, sDate, eDate) => {
    try {
      setIsLoading(true);
      const response = await getAllClassrooms({
        pageNumber: pageNo - 1,
        pageSize: pageSize,
        search: searchedText,
        status: status,
        startDateTime: sDate,
        endDateTime: eDate,
      });
      if (response?.data?.classRooms) {
        setClassrooms(
          response?.data?.classRooms.map((classroom) => {
            return {
              ...classroom,
              studentsEnrolled: classroom?.studentsCount
                ? classroom?.studentsCount
                : 0,
            };
          })
        );
        setNumOfPage(Math.ceil(response?.data?.count / pageSize));
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching classrooms:", error.message);
    }
  };

  useEffect(() => {
    const status = selectedStatus.join(",");
    getClassrooms(page, searchTerm, status, startDate, endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm, selectedStatus, startDate, endDate]);

  const deleteClassroomHandler = async () => {
    try {
      await deleteSingleClassroom(rowId);
      const status = selectedStatus.reduce(
        (stat, currentStatus) => stat + "," + currentStatus,
        ""
      );
      if (classrooms?.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        getClassrooms(page, searchTerm, status, startDate, endDate);
      }
      toast.success("Classroom Deleted Successfully");
    } catch (error) {
      toast.error("Classroom Delete Failed");
      console.error("Error deleting classroom:", error);
    }
  };

  const cloneClassroomHandler = async (id) => {
    try {
      await cloneClassroom(id);
      const status = selectedStatus.reduce(
        (stat, currentStatus) => stat + "," + currentStatus,
        ""
      );
      getClassrooms(page, searchTerm, status, startDate, endDate);
      toast.success("Classroom Cloned Successfully");
    } catch (error) {
      toast.error("Classroom Clone Failed");
      console.error("Error cloning classroom:", error);
    }
  };

  const deleteModalhandler = (id) => {
    setOpenDeleteModal(true);
    setRowId(id);
  };

  const editHandler = (id) => {
    navigate(`/classrooms/${id}/basic-information`);
  };

  const shareHandler = (id) => {
    setOpenShareModal(true);
    setRowId(id);
  };

  const showOrHideClassroomHandler = async (classroomDetails) => {
    setLoading(true);
    try {
      const payload = {
        showOnWebsite: !classroomDetails?.showOnWebsite,
      };

      const formData = new FormData();
      formData.append("jsonData", JSON.stringify(payload));

      const response = await updateClassroom(classroomDetails?._id, formData);
      if (response?.data?.error) {
        toast.error("Classroom Updation Failed");
      } else {
        toast.success(`Classroom Updated Successfully`);
        const cloneClassrooms = structuredClone(classrooms);
        const index = cloneClassrooms?.findIndex(
          (classRoom) => classRoom?._id === classroomDetails?._id
        );
        cloneClassrooms[index].showOnWebsite =
          !cloneClassrooms[index].showOnWebsite;
        setClassrooms(cloneClassrooms);
      }
      setLoading(false);
    } catch (error) {
      toast.error("Classroom Updation Failed");
      console.error("Error updating classroom:", error);
    }
  };

  const optionsMenuList = [
    {
      text: "Edit",
      actionsHandler: editHandler,
      imgSrc: "/editIcon.svg",
      imgAltText: "edit-icon",
    },
    {
      text: "Share",
      actionsHandler: shareHandler,
      imgSrc: "/Share.svg",
      imgAltText: "share-icon",
    },
  ];

  if (permissions.create) {
    optionsMenuList.push({
      text: "Clone",
      actionsHandler: cloneClassroomHandler,
      imgSrc: "/Copy.svg",
      imgAltText: "copy-icon",
    });
  }
  optionsMenuList.push({
    text: "Delete",
    actionsHandler: deleteModalhandler,
    imgSrc: "/deleteIcon.svg",
    imgAltText: "delete-icon",
  });

  // or as you probably should do
  const hostURL = window.location.protocol
    .concat("//")
    .concat(window.location.host);

  return (
    <div style={{ margin: "10px" }}>
      {loading && <Loader />}
      {roleOfTheUser !== "Student" && (
        <>
          <DeleteModal
            open={openDeleteModal}
            onClose={setOpenDeleteModal}
            onSubmit={deleteClassroomHandler}
            buttonText={"Delete"}
            title={"Delete Classroom"}
            message={
              "Are you sure you want to delete this classroom? This action cannot be undone."
            }
          />
          <ShareModal
            open={openShareModal}
            onClose={setOpenShareModal}
            title={"Share this Classroom Link"}
            message={
              "Hi, We'd like to invite you to join our class! Click the link below to sign up and join the classroom:\n"
            }
            shareLink={`${hostURL}/register?classroomId=${rowId}`}
          />
          <div className="row">
            <div className="row heading-container" style={{ margin: "0px" }}>
              <div className="col-12 col-lg-8 d-flex justify-content-lg-start justify-content-between align-items-center" />
              <div className="col-12 col-lg-4 d-flex justify-content-lg-end justify-content-center align-items-center">
                {permissions.create && (
                  <StyledOutlineButton
                    disabled={!permissions.create}
                    onClick={() => {
                      navigate("/classrooms/create/basic-information");
                    }}
                    btnText={"Create Classroom"}
                  />
                )}
              </div>
            </div>
          </div>
          {/* Search bar and additional elements on larger screens */}
          <div className={`row d-lg-flex mt-2 align-items-center justify-content-end mb-4`}>
            <div className="col-lg-4 mx-lg-0 mx-md-4 d-flex align-items-center justify-content-end">
              <SearchBar
                handleSearch={handleInputChange}
                handleClick={handleSearch}
                value={searchText}
                handleKeyPress={handleKeyPress}
              />
              <FiltersDropdown
                dateProps={{
                  startDate,
                  endDate,
                  setEndDate,
                  setStartDate,
                }}
                statusProps={{
                  selectedStatus,
                  setSelectedStatus,
                }}
              />
            </div>
          </div>
        </>
      )}
      <div className="row my-classrooms-grid-row">
        {!isLoading &&
          classrooms.map((classroom, ind) => (
            <div
              id={`${ind} ${classroom?._id}`}
              className="col-12 col-sm-6 col-md-4 col-lg-3 my-classrooms-grid-col"
              style={{ marginBottom: "24px" }}
              key={classroom?._id}
            >
              <ClassroomCard
                category={classroom?.category?.name}
                classroomName={classroom?.name}
                id={classroom?._id}
                progressPercentage="61"
                classroomThumbnail={classroom?.thumbnail}
                roleOfTheUser={roleOfTheUser}
                classroom={classroom}
                optionsMenuList={optionsMenuList}
                permissions={permissions}
                isPermissionApplied
                userId={userInformation?._id}
                showOrHideClassroomHandler={showOrHideClassroomHandler}
              />
            </div>
          ))}
      </div>
      {isLoading && (
        <div
          class="d-flex align-items-center justify-content-center"
          style={{ height: `300px`, width: "100%" }}
        >
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {!isLoading && numOfPage > 1 && (
        <PaginationComponent count={numOfPage} page={page} setPage={setPage} />
      )}
    </div>
  );
};

export default memo(MyClassrooms);
